<script setup>
import XIcon from '@iconify-vue/ph/x';
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
    VisuallyHidden,
} from 'reka-ui';
import { computed, onBeforeUnmount, provide, ref, watch } from 'vue';

const isOpen = defineModel({ required: false, default: false });

const {
    size = 'medium',
    side = 'right',
    stableGutter = false,
    loading = false,
    resizableFooter = false,
    minFooterHeight = 120,
    footerStorageKey = null,
} = defineProps({
    size: {
        type: String,
        required: false,
    },
    side: {
        type: String,
        required: false,
    },
    stableGutter: {
        type: Boolean,
        required: false,
    },
    loading: {
        type: Boolean,
        required: false,
    },
    resizableFooter: {
        type: Boolean,
        required: false,
    },
    minFooterHeight: {
        type: [Number, String],
        required: false,
    },
    footerStorageKey: {
        type: String,
        required: false,
    },
});

const offscreenX = computed(() => (side === 'left' ? '-100%' : '100%'));

const footerHeight = ref(null);

const isResizing = ref(false);

function clampFooterHeight(height) {
    return Math.min(
        Math.max(height, Number(minFooterHeight)),
        window.innerHeight * 0.85,
    );
}

function initFooterHeight() {
    if (!resizableFooter) {
        return;
    }

    const stored = footerStorageKey
        ? Number(localStorage.getItem(footerStorageKey))
        : null;

    footerHeight.value = clampFooterHeight(stored || Number(minFooterHeight));
}

function startResize(event) {
    isResizing.value = true;

    const startY = event.clientY;
    const startHeight = event.currentTarget.parentNode.offsetHeight;

    function onMove(moveEvent) {
        footerHeight.value = clampFooterHeight(
            startHeight + (startY - moveEvent.clientY),
        );
    }

    function onUp() {
        isResizing.value = false;

        if (footerStorageKey) {
            localStorage.setItem(footerStorageKey, footerHeight.value);
        }

        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
}

watch(
    isOpen,
    (value) => {
        if (value) {
            initFooterHeight();
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    isResizing.value = false;
});

provide('isInDialog', true);
provide('isInDialogDrawer', true);
</script>

<template>
    <DialogRoot v-model:open="isOpen">
        <DialogTrigger v-if="$slots.trigger" as-child>
            <slot name="trigger" />
        </DialogTrigger>

        <DialogPortal>
            <AnimatePresence>
                <Motion
                    v-if="isOpen"
                    :initial="{ opacity: 0 }"
                    :animate="{ opacity: 1 }"
                    :exit="{ opacity: 0 }"
                    :transition="{ duration: 0.2 }"
                    class="fixed inset-0 z-50"
                >
                    <DialogOverlay class="size-full bg-black/75" />
                </Motion>

                <Motion
                    v-if="isOpen"
                    :initial="{ x: offscreenX }"
                    :animate="{ x: '0%' }"
                    :exit="{ x: offscreenX }"
                    :transition="{
                        type: 'spring',
                        duration: 0.35,
                        bounce: 0,
                    }"
                    class="fixed top-0 z-50 flex h-full w-full overflow-y-auto"
                    :class="{
                        'right-0': side === 'right',
                        'left-0': side === 'left',
                        'md:w-10/12': size === 'almost-full',
                        'md:w-[48rem]': size === 'extra-large',
                        'md:w-[34rem]': size === 'large',
                        'md:w-[28rem]': size === 'medium',
                        'md:w-[24rem]': size === 'small',
                    }"
                >
                    <DialogContent
                        class="flex h-full w-full flex-col overflow-hidden bg-gray-100 shadow-xl"
                        :class="{ 'select-none': isResizing }"
                        :aria-describedby="undefined"
                    >
                        <div
                            class="relative flex items-center justify-start border-b border-gray-200 bg-white px-6 py-4"
                        >
                            <VisuallyHidden>
                                <DialogTitle>
                                    <slot name="title" />
                                </DialogTitle>
                            </VisuallyHidden>

                            <div class="h-6 truncate pr-8 font-medium">
                                <slot v-if="!loading" name="title" />
                            </div>

                            <div
                                v-if="!loading && $slots['header-actions']"
                                class="absolute right-16"
                            >
                                <slot name="header-actions" />
                            </div>

                            <div
                                class="absolute top-1/2 right-6 -translate-y-1/2"
                            >
                                <DialogClose
                                    :as="ActionButton"
                                    :icon="XIcon"
                                    square
                                    type="bordered"
                                    size="small"
                                />
                            </div>
                        </div>

                        <div v-if="loading" class="px-6 py-4">
                            <LoadingSpinner />
                        </div>

                        <template v-else>
                            <div
                                v-if="$slots['full-content']"
                                class="h-full overflow-y-auto"
                            >
                                <slot name="full-content" />
                            </div>

                            <div
                                v-if="$slots.content"
                                class="h-full overflow-y-auto px-6 py-4"
                                :style="
                                    stableGutter
                                        ? 'scrollbar-gutter:stable;'
                                        : ''
                                "
                            >
                                <slot name="content" />
                            </div>

                            <div
                                v-if="$slots.footer"
                                class="relative mt-auto flex shrink-0 flex-col overflow-y-auto border-t border-gray-200 bg-white px-6 py-4"
                                :style="
                                    resizableFooter && footerHeight
                                        ? `height:${footerHeight}px;`
                                        : ''
                                "
                            >
                                <div
                                    v-if="resizableFooter"
                                    class="group absolute inset-x-0 top-0 flex h-2 -translate-y-1/2 cursor-row-resize items-center justify-center"
                                    @mousedown.prevent="startResize"
                                >
                                    <div
                                        class="h-1 w-10 rounded-full bg-gray-300 transition-colors group-hover:bg-gray-400"
                                        :class="{ 'bg-gray-400': isResizing }"
                                    />
                                </div>

                                <slot name="footer" />
                            </div>
                        </template>
                    </DialogContent>
                </Motion>
            </AnimatePresence>
        </DialogPortal>
    </DialogRoot>
</template>
